// BeneAIMarkdown — shared renderer for any BeneAI-authored text.
//
// Both the comment-thread reply rows (CommitmentCommentsPanel) and
// the drawer assistant bubbles (BeneAIMessageList) feed model output
// into this component. The model emits markdown (`**bold**`, `*em*`,
// bullets, links, fenced code) and the previous plain-text renderer
// showed it verbatim — see the agent reply that read
// "Due **May 20, 2026**" as literal asterisks.
//
// react-markdown is the canonical React markdown renderer; it does
// NOT pass through raw HTML by default (no need for a separate
// sanitizer for the assistant payloads we control). remark-gfm
// adds GitHub-flavored extensions: tables, strikethrough, autolinks,
// task lists. The component overrides a few node renderers so output
// inherits the design-system typography (no zebra-stripe tables, no
// blown-up <h1> inside a chat bubble, links open in a new tab).

import ReactMarkdown from "react-markdown";
import type { Components } from "react-markdown";
import remarkGfm from "remark-gfm";


interface BeneAIMarkdownProps {
  /** Raw markdown produced by BeneAI. */
  body: string;
  /** Extra Tailwind classes applied to the wrapping container. The
   *  caller controls font size + color; the renderer only owns
   *  *structure* (paragraph spacing, list indent, code block frame). */
  className?: string;
  /** Optional testid surfaced on the wrapper so snapshot tests can
   *  scope queries. */
  "data-testid"?: string;
}


// Pinned at module scope so renders don't allocate a fresh map on
// every keystroke / re-render.
const COMPONENTS: Components = {
  // Paragraphs — keep tight vertical rhythm; first paragraph has no
  // top margin so the bubble doesn't gain a phantom row of padding.
  p({ children }) {
    return (
      <p className="mb-2 last:mb-0 whitespace-pre-wrap break-words">
        {children}
      </p>
    );
  },
  // Headings — chat surfaces aren't documents. Render H1-H3 the
  // same way: bold, slightly larger, no doc-style spacing.
  h1({ children }) {
    return (
      <p className="mb-2 mt-1 font-semibold text-text-primary">{children}</p>
    );
  },
  h2({ children }) {
    return (
      <p className="mb-2 mt-1 font-semibold text-text-primary">{children}</p>
    );
  },
  h3({ children }) {
    return (
      <p className="mb-2 mt-1 font-semibold text-text-primary">{children}</p>
    );
  },
  // Strong / em — preserve the design system's emphasis without
  // forcing italic outline on a small bubble.
  strong({ children }) {
    return <span className="font-semibold text-text-primary">{children}</span>;
  },
  em({ children }) {
    return <span className="italic">{children}</span>;
  },
  // Lists — keep them compact for chat density.
  ul({ children }) {
    return (
      <ul className="mb-2 list-disc space-y-0.5 pl-5 last:mb-0">
        {children}
      </ul>
    );
  },
  ol({ children }) {
    return (
      <ol className="mb-2 list-decimal space-y-0.5 pl-5 last:mb-0">
        {children}
      </ol>
    );
  },
  // Code: react-markdown v9 dropped the legacy ``inline`` prop in
  // favor of letting the caller distinguish via ``className`` (fenced
  // blocks carry a ``language-X`` hint; inline code does not). We
  // override the ``pre`` renderer separately so fenced blocks land
  // in a horizontally-scrollable frame.
  code({ children, className }) {
    const isFenced = typeof className === "string"
      && className.startsWith("language-");
    if (!isFenced) {
      return (
        <code className="rounded bg-recessed px-1 py-0.5 font-mono text-[0.85em]">
          {children}
        </code>
      );
    }
    return (
      <code className={`block font-mono text-[0.85em] ${className}`}>
        {children}
      </code>
    );
  },
  pre({ children }) {
    return (
      <pre className="mb-2 overflow-x-auto rounded-md bg-recessed p-2 last:mb-0">
        {children}
      </pre>
    );
  },
  // Links — open in a new tab, never inherit the chat surface
  // bubble background.
  a({ href, children }) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-teal underline-offset-2 hover:underline"
      >
        {children}
      </a>
    );
  },
  // Tables (remark-gfm). Chat bubbles are narrow, so wrap in a
  // scroll container so wide tables don't overflow.
  table({ children }) {
    return (
      <div className="mb-2 overflow-x-auto last:mb-0">
        <table className="border-collapse text-[0.85em]">{children}</table>
      </div>
    );
  },
  th({ children }) {
    return (
      <th className="border-b border-border-subtle px-2 py-1 text-left font-semibold">
        {children}
      </th>
    );
  },
  td({ children }) {
    return (
      <td className="border-b border-border-subtle/60 px-2 py-1">
        {children}
      </td>
    );
  },
  // Block quotes — used occasionally by the model for callouts.
  blockquote({ children }) {
    return (
      <blockquote className="mb-2 border-l-2 border-border-subtle pl-3 italic text-text-secondary last:mb-0">
        {children}
      </blockquote>
    );
  },
  // Horizontal rule.
  hr() {
    return <hr className="my-2 border-border-subtle/60" />;
  },
};


export function BeneAIMarkdown({
  body, className, "data-testid": testId,
}: BeneAIMarkdownProps) {
  return (
    <div
      className={className}
      data-testid={testId}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={COMPONENTS}
        // react-markdown disables raw HTML by default; explicit here
        // so a future caller doesn't get surprised by an injected
        // ``rehypeRaw``.
        skipHtml
      >
        {body}
      </ReactMarkdown>
    </div>
  );
}
